import React, { useEffect, useState } from 'react';
import PhoneInput from 'react-phone-input-2';
import Modal from '../../../../../shared/components/core-ui/modal/modal.component';
import Button, {
  ButtonSize,
  ButtonType
} from '../../../../../shared/components/core-ui/button/button.component';
import useWindowResizer from '../../../../../shared/hooks/window-resizer/window-resizer.hook';
import { ReservationDetailsUiModel } from '../../../../../shared/ui-models/reservation.ui-model';
import { UserUIModel } from '../../../../../shared/ui-models/user.ui-model';
import { ErrorInfo, isErrorInfo } from '../../../../../shared/interfaces/error-info.interface';
import { getUserByEmail, getUserByPhone } from '../../../../../shared/services/data/user.data';
import { getModuleName } from '../../../../shared/services/reservation.service';

export interface ChangeAccountModalProps {
  config: {
    showModal: boolean;
  };
  data: {
    reservation: ReservationDetailsUiModel | undefined;
    isLoading: boolean;
    errorMessage?: string;
  };
  eventHandlers: {
    closeHandler: () => void;
    submitHandler: (userId: string) => void;
  };
}

type SearchBy = 'email' | 'phone';

/**
 * functional component ChangeAccountModal
 * Finds a guest account by email or phone and links the reservation to it.
 */
export const ChangeAccountModal: React.FC<ChangeAccountModalProps> = ({
  config,
  data,
  eventHandlers
}) => {
  const isMobileView = useWindowResizer();
  const [searchBy, setSearchBy] = useState<SearchBy>('email');
  const [email, setEmail] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [searchError, setSearchError] = useState<string | undefined>(undefined);
  const [users, setUsers] = useState<UserUIModel[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserUIModel | undefined>(undefined);

  useEffect(() => {
    if (!config.showModal) {
      setEmail('');
      setPhone('');
      setUsers([]);
      setSelectedUser(undefined);
      setSearchError(undefined);
    }
  }, [config.showModal]);

  const searchValue = searchBy === 'email' ? email.trim() : phone;

  const handleSearch = () => {
    if (!searchValue) return;
    setIsSearching(true);
    setSearchError(undefined);
    setUsers([]);
    setSelectedUser(undefined);
    const search =
      searchBy === 'email'
        ? getUserByEmail(searchValue, getModuleName(), { returnAll: true })
        : getUserByPhone(searchValue, getModuleName(), { returnAll: true });
    search.then((response: UserUIModel[] | ErrorInfo) => {
      setIsSearching(false);
      if (isErrorInfo(response) || response.length === 0) {
        setSearchError('No account found');
        return;
      }
      setUsers(response);
      if (response.length === 1) setSelectedUser(response[0]);
    });
  };

  const isSameAccount = !!selectedUser && selectedUser.id === data.reservation?.userId;

  return (
    <Modal
      config={{ showModal: config.showModal, fullScreen: false, showClose: true, maxWidth: 'sm' }}
      eventHandlers={{ closeModalHandler: () => eventHandlers.closeHandler() }}
    >
      <div className="flex flex-col p-[24px] md:px-[48px] md:py-[36px] min-w-[320px] md:min-w-[480px] font-gilroy-regular text-neutral-500">
        <h2 className="m-0 text-[24px] font-gilroy-bold">Change account</h2>
        <p className="mt-[6px] mb-0 text-[15px] text-neutral-300">
          Link this reservation to another guest account. The guest's name, email and phone on the
          reservation will be replaced with the new account's details.
        </p>

        {data.reservation && (
          <div className="mt-[20px] rounded-[12px] bg-creams-100 px-[16px] py-[12px]">
            <div className="text-[13px] text-neutral-300">Current account</div>
            <div className="text-[15px] font-gilroy-medium truncate">
              {data.reservation.primaryGuestName || '—'}
            </div>
            <div className="text-[13px] text-neutral-300 truncate">
              {data.reservation.primaryGuestEmail || ''} · ID {data.reservation.userId || '—'}
            </div>
          </div>
        )}

        <div className="mt-[20px] flex gap-[8px]">
          {(['email', 'phone'] as SearchBy[]).map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => {
                setSearchBy(option);
                setUsers([]);
                setSelectedUser(undefined);
                setSearchError(undefined);
              }}
              className={`px-[16px] py-[6px] rounded-full border text-[14px] font-gilroy-medium cursor-pointer ${
                searchBy === option
                  ? 'bg-neutral-500 border-neutral-500 text-neutral-0'
                  : 'border-gray-200 text-neutral-500 hover:border-neutral-500'
              }`}
            >
              {option === 'email' ? 'Email' : 'Phone'}
            </button>
          ))}
        </div>

        <div className="mt-[12px] flex items-center gap-[8px]">
          <div className="flex-1 min-w-0">
            {searchBy === 'email' ? (
              <input
                type="email"
                value={email}
                placeholder="guest@example.com"
                onChange={(event) => setEmail(event.target.value)}
                onKeyDown={(event) => event.key === 'Enter' && handleSearch()}
                className="w-full h-[40px] rounded-[10px] border border-gray-200 px-[12px] text-[15px]"
              />
            ) : (
              <PhoneInput
                country={'eg'}
                value={phone}
                onChange={(value) => setPhone(value ? `+${value}` : '')}
              />
            )}
          </div>
          <Button
            onClick={handleSearch}
            loading={isSearching}
            disabled={!searchValue || isSearching}
            tracking={{ event: 'search_performed', scope: 'user' }}
            size={ButtonSize.medium}
          >
            Search
          </Button>
        </div>

        {searchError && <div className="mt-[12px] text-[14px] text-red-600">{searchError}</div>}

        {users.length > 0 && (
          <div className="mt-[16px] flex flex-col gap-[8px] max-h-[240px] overflow-y-auto">
            {users.map((user) => (
              <button
                key={user.id}
                type="button"
                onClick={() => setSelectedUser(user)}
                className={`text-left rounded-[12px] border px-[16px] py-[10px] cursor-pointer ${
                  selectedUser?.id === user.id
                    ? 'border-neutral-500 bg-creams-100'
                    : 'border-gray-200 hover:border-neutral-500'
                }`}
              >
                <div className="text-[15px] font-gilroy-medium truncate">{user.name || '—'}</div>
                <div className="text-[13px] text-neutral-300 truncate">
                  {[user.email, user.phone].filter(Boolean).join(' · ')}
                </div>
                {user.id === data.reservation?.userId && (
                  <div className="text-[12px] text-neutral-300">
                    Already linked to this reservation
                  </div>
                )}
              </button>
            ))}
          </div>
        )}

        {data.errorMessage && (
          <div className="mt-[16px] text-[14px] text-red-600">&#9888; {data.errorMessage}</div>
        )}

        <div className="mt-[24px] flex justify-end gap-[12px] flex-wrap">
          <Button
            onClick={() => eventHandlers.closeHandler()}
            type={ButtonType.primaryInverted}
            tracking={{ event: 'record_edit_cancelled', entity: 'reservation', method: 'close' }}
            size={isMobileView ? ButtonSize.full : ButtonSize.medium}
          >
            Cancel
          </Button>
          <Button
            loading={data.isLoading}
            disabled={!selectedUser?.id || isSameAccount || data.isLoading}
            onClick={() => selectedUser?.id && eventHandlers.submitHandler(selectedUser.id)}
            tracking={{
              event: 'record_saved',
              entity: 'reservation',
              action: 'update',
              outcome: 'success'
            }}
            size={isMobileView ? ButtonSize.full : ButtonSize.medium}
          >
            Link account
          </Button>
        </div>
      </div>
    </Modal>
  );
};
